import {
  createContext,
  type ReactElement,
  type ReactNode,
  useContext,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

import * as styles from './bottom-bar.css';

const WorkspacePaneBottomBarHostContext = createContext<HTMLElement | null>(
  null
);
const WorkspacePaneBottomBarAvailableContext = createContext(false);

/** Whether the view sits in a surface with a bar slot; phone views have none. */
export function useWorkspacePaneBottomBarAvailable(): boolean {
  return useContext(WorkspacePaneBottomBarAvailableContext);
}

/** The surface's slot for its bottom bars; WorkspacePaneSurface renders it around the view. */
export function WorkspacePaneBottomBarSlot({
  children,
}: {
  children?: ReactNode;
}): ReactElement {
  const [host, setHost] = useState<HTMLDivElement | null>(null);

  return (
    <>
      <WorkspacePaneBottomBarAvailableContext.Provider value>
        <WorkspacePaneBottomBarHostContext.Provider value={host}>
          {children}
        </WorkspacePaneBottomBarHostContext.Provider>
      </WorkspacePaneBottomBarAvailableContext.Provider>
      <div
        className={styles.bottomBar}
        data-workspace-pane-bottom-bar=""
        ref={setHost}
      />
    </>
  );
}

/**
 * The view's bar at its bottom (selection actions, status): rendered into the surface's bar slot,
 * centred in the view and kept just above the AI island's dock row, so the island never covers it.
 * Works from any depth of the view; renders nothing without content or outside a surface.
 */
export function WorkspacePaneBottomBar({
  children,
  testId,
}: {
  children?: ReactNode;
  testId?: string;
}): ReactElement | null {
  const host = useContext(WorkspacePaneBottomBarHostContext);
  if (!host || !children) return null;

  return createPortal(
    <div className={styles.bottomBarItem} data-testid={testId}>
      {children}
    </div>,
    host
  );
}
